import { cn } from '@/lib/cn';
import type { ChatModel } from '@innermate/shared';
import { Check, ChevronDown, Link2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  models: ChatModel[];
  selected: ChatModel | null;
  extendedThinking: boolean;
  onSelect: (m: ChatModel) => void;
  onToggleThinking: (v: boolean) => void;
  disabled?: boolean;
}

export function ModelPicker({
  models,
  selected,
  extendedThinking,
  onSelect,
  onToggleThinking,
  disabled,
}: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ bottom: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    window.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const toggle = () => {
    if (disabled) return;
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        bottom: window.innerHeight - rect.top + 6,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen((v) => !v);
  };

  if (!selected) {
    return (
      <button
        type="button"
        disabled
        className="h-8 px-3 rounded-full text-xs text-foreground-muted"
      >
        모델 없음
      </button>
    );
  }

  const showThinkingBadge = selected.supportsExtendedThinking && extendedThinking;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'h-8 px-3 rounded-full text-sm inline-flex items-center gap-1.5',
          'text-foreground hover:bg-surface-muted transition-colors',
          'disabled:opacity-40 disabled:cursor-not-allowed',
        )}
      >
        {selected.kind === 'bonding' ? (
          <Link2 className="size-3.5 text-foreground-muted" aria-label="본딩" />
        ) : null}
        <span className="font-medium">{selected.displayName}</span>
        {showThinkingBadge ? <span className="text-foreground-muted text-xs">적응형</span> : null}
        <ChevronDown className="size-3.5 text-foreground-muted" />
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              // biome-ignore lint/a11y/useSemanticElements: <select> 로 교체하면 portal/스타일이 깨져 커스텀 listbox 유지
              role="listbox"
              tabIndex={-1}
              style={{ position: 'fixed', bottom: pos.bottom, right: pos.right }}
              className="w-72 z-50 rounded-xl border border-border bg-surface shadow-lg py-1 text-sm text-foreground"
            >
              {models.map((m) => {
                const isSelected = m.id === selected.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    // biome-ignore lint/a11y/useSemanticElements: <option> 은 <select> 안에서만 유효 — 커스텀 listbox 패턴
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      onSelect(m);
                      setOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-surface-muted"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {m.kind === 'bonding' ? (
                          <Link2
                            className="size-3.5 text-foreground-muted shrink-0"
                            aria-label="본딩"
                          />
                        ) : null}
                        <span className="font-medium truncate">{m.displayName}</span>
                        {m.kind === 'bonding' ? (
                          <span className="text-[10px] uppercase tracking-wider text-foreground-muted bg-surface-muted px-1.5 h-4 inline-flex items-center rounded-sm">
                            다중
                          </span>
                        ) : null}
                      </div>
                      {isSelected ? (
                        <Check className="size-4 text-accent shrink-0" aria-label="선택됨" />
                      ) : null}
                    </div>
                    {m.description ? (
                      <div className="mt-0.5 text-xs text-foreground-muted line-clamp-1">
                        {m.description}
                      </div>
                    ) : null}
                  </button>
                );
              })}
              {selected.supportsExtendedThinking ? (
                <>
                  <div className="my-1 border-t border-border" />
                  <div className="px-3 py-2 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium">적응형 사고</div>
                      <div className="mt-0.5 text-xs text-foreground-muted">
                        더 복잡한 작업을 위해 사고합니다
                      </div>
                    </div>
                    <Toggle
                      checked={extendedThinking}
                      onChange={onToggleThinking}
                      ariaLabel="적응형 사고"
                    />
                  </div>
                </>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function Toggle({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors',
        checked ? 'bg-accent' : 'bg-surface-muted',
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 size-4 rounded-full bg-white transition-transform',
          checked ? 'translate-x-[18px]' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}
